<template>
	<app-page>
		<app-tabs app :tabs="tabs">
			<template v-slot:tab-item="{ tab }">
				<app-tab-item :tab="tab" value="failed">
					<app-heading title="Failed Jobs">
						<template v-slot:right>
							<app-button
								outlined
								small
								color="secondary"
								icon="fa-sync"
								@click="refresh = true"
							>Refresh</app-button>
						</template>
					</app-heading>
					<app-row>
						<app-cell>
							<div>
								<app-button-confirm
										v-if="$ravello.can('system.post')"
										color="warning"
										icon="fa-redo"
										small
										label-confirm="Retry"
										:description="`Are you sure you want to retry selected jobs?`"
										title="Retry Jobs"
										@click="onRetrySelected()"
										:disabled="!selected || !selected.length"
									>
										Retry
									</app-button-confirm>
								<app-button-confirm
										v-if="$ravello.can('system.delete')"
										color="error"
										icon="fa-trash"
										small
										label-confirm="Remove"
										:description="`Are you sure you want to remove selected jobs?`"
										title="Remove Jobs"
										@click="onDeleteSelected()"
										:disabled="!selected || !selected.length"
									>
										Remove
									</app-button-confirm>
							</div>
							<app-data-table
								:items="jobs"
								:headers="headers"
								:total="count"
								:refresh.sync="refresh"
								@update="fetchJobs"
								@select="updateSelection"
								select
								primaryKey="id"
							>
								<template v-slot:data="{ item }">
									<app-json :data="item.data" />
								</template>
								<template v-slot:error="{ item }">
									<app-button
										table
										color="secondary"
										@click="loadError(item.id)"
									>View</app-button>
								</template>
								<template v-slot:actions="{ item }">
									<app-button
										v-if="$ravello.can('system.post')"
										color="secondary"
										icon="fa-terminal"
										table
										@click="onTrail(item.id)"
									>
										Trail
									</app-button>
									<app-button-confirm
										v-if="$ravello.can('system.post')"
										color="warning"
										icon="fa-redo"
										table
										label-confirm="Retry"
										:description="`Are you sure you want to retry job <b>${item.id}</b>?`"
										title="Retry Job"
										@click="onRetry(item.id)"
									>
										Retry
									</app-button-confirm>
									<app-button-confirm
										v-if="$ravello.can('system.delete')"
										color="error"
										icon="fa-trash"
										table
										label-confirm="Remove"
										:description="`Are you sure you want to remove job
											<b>${item.id}</b>?`"
										title="Remove Job"
										@click="onDelete(item.id)"
									>
										Remove
									</app-button-confirm>
								</template>
							</app-data-table>
						</app-cell>
					</app-row>

					<app-dialog
						v-model="error.show"
						max-width="600"
						height="400"
						max-height="400"
						title="Error"
					>
						<app-dialog-body>
							<app-json v-if="typeof error.content === 'object'" :data="error.content" open />
							<div v-else>{{error.content}}</div>
						</app-dialog-body>
						<app-dialog-footer>
							<app-button
								flat="flat"
								@click="error.show = false"
							>Close</app-button>
						</app-dialog-footer>
					</app-dialog>
					<app-dialog v-model="trails.show" max-width="600" title="Trail">
						<app-dialog-body class="jobs-failed-trail pt-2">
							<div class="console">
								<app-row v-for="(trail, i) in trails.data" :key="`trail-${i}`">
									<app-cell class="pl-1 pr-1 pb-0 pt-1" cols="12">
										<span v-if="/ERRORED/.test(trail)" class="error--text">{{trail}}</span>
										<span v-else>{{trail}}</span>
									</app-cell>
								</app-row>
							</div>
						</app-dialog-body>
						<app-dialog-footer>
							<app-button
								color="primary"
								flat="flat"
								@click="trails.show = false"
							>
								Close
							</app-button>
						</app-dialog-footer>
					</app-dialog>
				</app-tab-item>
			</template>
		</app-tabs>
	</app-page>
</template>

<script>
import { jobsTabs } from '@/datasources/tabs'

export default {
	data() {
		return {
			jobs: [],
			selected: [],
			tabs: jobsTabs,
			trails: {
				show: false,
				id: null,
				data: [],
			},
			error: {
				show: false,
				content: {},
			},
			count: -1,
			refresh: false,
			headers: [{
				text: 'ID',
				value: 'id',
				sortable: false,
			}, {
				text: 'Event',
				value: 'name',
				sortable: false,
			}, {
				text: 'Data',
				value: 'data',
				sortable: false,
			}, {
				text: 'Error',
				value: 'error',
				sortable: false,
			}, {
				text: 'Actions',
				value: 'actions',
				align: 'end',
				sortable: false,
			}],
		}
	},
	methods: {
		async loadError(id) {
			this.error.show = true
			this.error.content = 'Loading'
			try {
				const error = await this.$ravello.request()
					.to('system')
					.to('jobs')
					.to('failed')
					.to(id)
					.to('error')
					.get()
				this.error.content = error
			} catch (err) {
				if (err.status !== '404') {
					this.$notifications.pushAlert('error', err.data.message)
				}
				this.error.content = 'Not found'
			}
		},
		async fetchJobs(pagination) {
			const request = this.$ravello.request()
				.to('system')
				.to('jobs')
				.to('failed')
				.fromPagination(pagination)

			try {
				this.jobs = await request.get()
				if (this.count === -1) {
					const count = await request.count()
					this.count = count.count
				}
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},
		updateSelection(selected) {
			this.selected = selected
		},
		async onRetry(id) {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.to('system')
					.to('jobs')
					.to('failed')
					.to(id)
					.to('retry')
					.create()
				this.refresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},

		async fetchTrail(id) {
			const request = this.$ravello.request()
				.to('system')
				.to('jobs')
				.to('failed')
				.to(id)
				.to('trail')

			try {
				this.trails.data = await request.get()
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},

		async onTrail(id) {
			this.trails.show = true
			this.trails.id = id
			this.trails.data = []

			await this.fetchTrail(id)
		},
		async onDelete(id) {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.to('system')
					.to('jobs')
					.to('failed')
					.to(id)
					.delete()
				this.refresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},
		async onDeleteSelected() {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.to('system')
					.to('jobs')
					.to('failed')
					.delete({ jobs: this.selected })
				this.refresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},
		async onRetrySelected() {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.to('system')
					.to('jobs')
					.to('failed')
					.to('retry')
					.create({ jobs: this.selected })
				this.refresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},
	},
}
</script>

<style>
	/* @todo - Maybe have a component for that */
	.jobs-failed-trail .console {
		background: black;
		color: gray;
		height: 600px;
		max-height: 600px;
		overflow-x: hidden;
    overflow-y: auto;
    word-break: break-word;
	}
</style>
